import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Badge,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { providerAPI } from '../services/api';
import { Provider } from '../types';
import ProviderVehiclesSection from './ProviderVehiclesSection';

const Profile: React.FC = () => {
  const { provider: authProvider, isAuthenticated, isLoading: authLoading } = useAuth();
  const [profileData, setProfileData] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!isAuthenticated || !authProvider?.provider_id) {
        setLoading(false);
        return;
      }

      try {
        console.log('Profile: Fetching complete profile data...');
        console.log('Profile: Provider ID:', authProvider.provider_id);
        const data = await providerAPI.getProvider(authProvider.provider_id);
        console.log('Profile: Fetched profile data:', data);
        setProfileData(data);
      } catch (error) {
        console.error('Profile: Error fetching profile data:', error);
        setError('Failed to load profile information');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [isAuthenticated, authProvider]);

  console.log('Profile: authLoading:', authLoading);
  console.log('Profile: isAuthenticated:', isAuthenticated);
  console.log('Profile: authProvider:', authProvider);
  console.log('Profile: profileData:', profileData);
  console.log('Profile: loading:', loading);

  if (authLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
          Profile
        </Typography>
        <Typography variant="body1" sx={{ color: '#2D4B73' }}>
          Loading profile information...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated || !authProvider) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
          Profile
        </Typography>
        <Typography variant="body1" sx={{ color: '#ef4444' }}>
          Please log in to view your profile information.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
          My Profile
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
          My Profile
        </Typography>
        <Typography variant="body1" sx={{ color: '#ef4444' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  // Use profileData if available, otherwise fall back to authProvider
  const provider = profileData || authProvider;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
        My Profile
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: '#2D4B73' }}>
        Your profile information. Contact the administrator to update your details.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ 
            p: 3,
            borderRadius: 16,
            backgroundColor: '#ffffff',
            border: '1px solid rgba(45, 75, 115, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
          }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2D4B73' }}>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 3, borderColor: 'rgba(45, 75, 115, 0.3)' }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Person sx={{ mr: 1, color: '#2D4B73' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                    Name
                  </Typography>
                </Box>
                                 <Typography variant="h6" sx={{ color: '#2D4B73', fontWeight: 600 }}>
                   {`${provider.first_name} ${provider.last_name}`.trim() || 'Not available'}
                 </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Email sx={{ mr: 1, color: '#2D4B73' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                    Email
                  </Typography>
                </Box>
                                 <Typography variant="h6" sx={{ color: '#2D4B73', fontWeight: 600 }}>
                   {provider.email || 'Not available'}
                 </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Phone sx={{ mr: 1, color: '#2D4B73' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                    Phone
                  </Typography>
                </Box>
                                 <Typography variant="h6" sx={{ color: '#2D4B73', fontWeight: 600 }}>
                   {provider.phone || 'Not available'}
                 </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Badge sx={{ mr: 1, color: '#2D4B73' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                    License Number
                  </Typography>
                </Box>
                                 <Typography variant="h6" sx={{ color: '#2D4B73', fontWeight: 600 }}>
                   Provider #{provider.provider_id} (NPI: {provider.npi_number || 'Not assigned'})
                 </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            borderRadius: 16,
            backgroundColor: '#ffffff',
            border: '1px solid rgba(45, 75, 115, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2D4B73' }}>
                Account Information
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'rgba(45, 75, 115, 0.3)' }} />
              
                             <Box mb={2}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                   Member Since
                 </Typography>
                 <Typography variant="body1" sx={{ color: '#2D4B73' }}>
                   {(() => {
                     const creationDate = provider.created_at;
                     console.log('Profile: creationDate value:', creationDate);
                     console.log('Profile: creationDate type:', typeof creationDate);
                     
                     if (!creationDate) {
                       return 'Not available';
                     }
                     
                     try {
                       const date = new Date(creationDate);
                       console.log('Profile: parsed creationDate:', date);
                       if (isNaN(date.getTime())) {
                         return 'Not available';
                       }
                       return format(date, 'MMM dd, yyyy');
                     } catch (error) {
                       console.error('Profile: Error parsing creationDate:', error);
                       return 'Not available';
                     }
                   })()}
                 </Typography>
               </Box>

               <Box mb={2}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                   Last Updated
                 </Typography>
                 <Typography variant="body1" sx={{ color: '#2D4B73' }}>
                   {(() => {
                     const updateDate = provider.updated_at;
                     console.log('Profile: updateDate value:', updateDate);
                     console.log('Profile: updateDate type:', typeof updateDate);
                     
                     if (!updateDate) {
                       return 'Not available';
                     }
                     
                     try {
                       const date = new Date(updateDate);
                       console.log('Profile: parsed updateDate:', date);
                       if (isNaN(date.getTime())) {
                         return 'Not available';
                       }
                       return format(date, 'MMM dd, yyyy');
                     } catch (error) {
                       console.error('Profile: Error parsing updateDate:', error);
                       return 'Not available';
                     }
                   })()}
                 </Typography>
               </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                  Account Status
                </Typography>
                <Chip 
                  label="Active" 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 600, mt: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {authProvider?.provider_id != null && (
        <ProviderVehiclesSection providerId={Number(authProvider.provider_id)} />
      )}
    </Box>
  );
};

export default Profile;
